import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { getImageUrl } from '../../services/api';
import './UserDashboard.css';

interface Ownership {
  id: number;
  flat_number: string;
  ownership_type: string; // 'owner' | 'renter' | 'resident' | etc.
  ownership_percentage?: number;
  building?: { id: number; name: string };
  flat?: { id: number; flat_type?: string };
}

interface User {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
  profile_picture?: string | null;
  flat_no?: string | null;
  building?: { id: number; name: string };
  ownerships?: Ownership[];
}

interface FlatUser {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role: string;
  is_active?: boolean;
}

interface MembershipUser {
  id: number;
  name: string;
  email: string;
  phone?: string;
  role: string;
}

interface Membership {
  id: number;
  flat_id: number;
  building_id: number;
  user_id: number;
  relation_type: string;
  is_primary: boolean;
  status: 'pending' | 'approved' | 'removed';
  user?: MembershipUser;
}

const UserDashboard: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [membersByFlat, setMembersByFlat] = useState<Record<number, Membership[]>>({});
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersError, setMembersError] = useState<string | null>(null);

  // Add Member UI state
  const [addOpenForFlat, setAddOpenForFlat] = useState<number | null>(null);
  const [candidateUsers, setCandidateUsers] = useState<Record<number, FlatUser[]>>({});
  const [addForm, setAddForm] = useState<{ user_id: number | ''; relation_type: string }>({ user_id: '', relation_type: 'tenant' });

  // Registered users by flat (from /api/users?flat_id=...)
  const [registeredByFlat, setRegisteredByFlat] = useState<Record<number, FlatUser[]>>({});
  const [registeredLoading, setRegisteredLoading] = useState(false);
  const [registeredError, setRegisteredError] = useState<string | null>(null);
  // Owners per flat (to avoid flagging owners as ghosts)
  const [ownersByFlat, setOwnersByFlat] = useState<Record<number, number[]>>({});

  // View profile modal state
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileData, setProfileData] = useState<any>(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await api.get('/profile');
        if (res.data?.success) {
          setUser(res.data.data);
        } else {
          setError('Failed to load profile');
        }
      } catch (e: any) {
        setError(e.response?.data?.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const hasOwnershipType = (type: string) =>
    !!user?.ownerships?.some(o => (o.ownership_type || '').toLowerCase() === type);

  const isOwner = hasOwnershipType('owner');
  const isRenter = hasOwnershipType('renter');
  const isResident = user?.role === 'resident' || hasOwnershipType('resident');
  const isManager = user?.role === 'manager' || user?.role === 'building_committee';
  const isAdmin = user?.role === 'admin' || user?.role === 'central_committee';

  // Fetch memberships for each owned flat
  useEffect(() => {
    const loadMembers = async () => {
      if (!user || !isOwner || !user.ownerships?.length) return;
      const ownerFlats = user.ownerships.filter(o => (o.ownership_type || '').toLowerCase() === 'owner');
      const uniqueFlatIds = Array.from(new Set(ownerFlats.map(o => o.flat?.id).filter(Boolean) as number[]));
      if (uniqueFlatIds.length === 0) return;
      try {
        setMembersLoading(true);
        const results: Record<number, Membership[]> = {};
        // Fetch sequentially to keep it simple and avoid rate spikes
        for (const fid of uniqueFlatIds) {
          const res = await api.get(`/flats/${fid}/members`);
          const raw = res?.data?.data ?? res?.data ?? [];
          results[fid] = Array.isArray(raw) ? raw as Membership[] : [];
        }
        setMembersByFlat(results);
      } catch (e: any) {
        setMembersError(e.response?.data?.message || 'Failed to load members for your flat');
      } finally {
        setMembersLoading(false);
      }
    };
    loadMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refreshMembers = async (flatId: number) => {
    try {
      const res = await api.get(`/flats/${flatId}/members`);
      const raw = res?.data?.data ?? res?.data ?? [];
      setMembersByFlat(prev => ({ ...prev, [flatId]: Array.isArray(raw) ? raw : [] }));
    } catch {}
  };

  const refreshRegisteredUsers = async (flatId: number) => {
    try {
      const res = await api.get('/users', { params: { per_page: 200, flat_id: flatId } });
      let list: any = res?.data?.data ?? res?.data ?? [];
      if (!Array.isArray(list) && Array.isArray(list?.data)) list = list.data;
      setRegisteredByFlat(prev => ({ ...prev, [flatId]: Array.isArray(list) ? list as FlatUser[] : [] }));
    } catch {}
  };

  const refreshOwners = async (flatId: number) => {
    try {
      const res = await api.get(`/flats/${flatId}/owners`);
      const data = res?.data?.data ?? res?.data ?? [];
      const ownerIds: number[] = Array.isArray(data) ? data.map((o: any) => o.user_id).filter((id: any) => typeof id === 'number') : [];
      setOwnersByFlat(prev => ({ ...prev, [flatId]: ownerIds }));
    } catch {}
  };

  const approveMember = async (flatId: number, memberId: number) => {
    await api.post(`/flats/${flatId}/members/${memberId}/approve`);
    await refreshMembers(flatId);
    await refreshRegisteredUsers(flatId);
    await refreshOwners(flatId);
  };

  const removeMember = async (flatId: number, memberId: number) => {
    await api.delete(`/flats/${flatId}/members/${memberId}`);
    await refreshMembers(flatId);
    await refreshRegisteredUsers(flatId);
    await refreshOwners(flatId);
  };

  const openAddMember = async (flatId: number, buildingId?: number) => {
    setAddOpenForFlat(flatId);
    setAddForm({ user_id: '', relation_type: 'tenant' });
    // Load candidate users for this building (and exclude already present users later)
    try {
      const res = await api.get('/users', { params: { per_page: 200, building_id: buildingId } });
      let list: any = res?.data?.data ?? res?.data ?? [];
      if (!Array.isArray(list) && Array.isArray(list?.data)) list = list.data;
      const candidates = Array.isArray(list) ? list as FlatUser[] : [];
      setCandidateUsers(prev => ({ ...prev, [flatId]: candidates }));
    } catch {
      setCandidateUsers(prev => ({ ...prev, [flatId]: [] }));
    }
  };

  const submitAddMember = async (flatId: number) => {
    if (!addForm.user_id) return;
    await api.post(`/flats/${flatId}/members`, {
      user_id: addForm.user_id,
      relation_type: addForm.relation_type,
    });
    setAddOpenForFlat(null);
    await refreshMembers(flatId);
    await refreshRegisteredUsers(flatId);
    await refreshOwners(flatId);
  };

  const viewMemberProfile = async (userId: number) => {
    try {
      setProfileOpen(true);
      setProfileLoading(true);
      setProfileError(null);
      setProfileData(null);
      const res = await api.get(`/profile/${userId}`);
      const data = res?.data?.data ?? res?.data ?? null;
      setProfileData(data);
    } catch (e: any) {
      setProfileError(e?.response?.data?.message || 'Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="dash-loading">
        <div className="dash-spinner" />
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="dash-error">
        <p>{error || 'Unable to load dashboard.'}</p>
        <Link to="/profile" className="btn btn-primary">Go to Profile</Link>
      </div>
    );
  }

  return (
    <div className="user-dash">
      <div className="dash-header">
        <div className="dash-avatar">
          {user.profile_picture ? (
            <img src={getImageUrl(user.profile_picture)} alt={user.name} />
          ) : (
            <div className="dash-avatar-placeholder"><i className="fas fa-user" /></div>
          )}
        </div>
        <div className="dash-meta">
          <h1>{user.name}</h1>
          <p className="muted">{user.email}</p>
          <div className="dash-badges">
            <span className="badge role">{user.role.replace('_', ' ')}</span>
            {isOwner && <span className="badge owner">Owner</span>}
            {isRenter && <span className="badge renter">Renter</span>}
            {isResident && <span className="badge resident">Resident</span>}
            {user.is_active ? <span className="badge active">Active</span> : <span className="badge inactive">Inactive</span>}
          </div>
        </div>
        <div className="dash-actions">
          <Link to="/profile" className="btn btn-primary"><i className="fas fa-user-cog" /> Profile</Link>
          {isAdmin && <Link to="/admin" className="btn btn-warning"><i className="fas fa-tools" /> Admin</Link>}
          {isManager && <Link to="/manager" className="btn btn-secondary"><i className="fas fa-building" /> Manager</Link>}
        </div>
      </div>

      <div className="dash-grid">
        <Link to="/notices" className="dash-card">
          <div className="icon"><i className="fas fa-bullhorn" /></div>
          <div className="content">
            <h3>Notices</h3>
            <p>See building and central notices</p>
          </div>
        </Link>

        <Link to="/profile" className="dash-card">
          <div className="icon"><i className="fas fa-id-card" /></div>
          <div className="content">
            <h3>My Profile</h3>
            <p>Update info and profile picture</p>
          </div>
        </Link>

        {(isOwner || isRenter || isResident) && (
          <Link to="/resident" className="dash-card">
            <div className="icon"><i className="fas fa-home" /></div>
            <div className="content">
              <h3>My Residence</h3>
              <p>Ownership, bills, complaints</p>
            </div>
          </Link>
        )}

        {isManager && (
          <Link to="/manager" className="dash-card">
            <div className="icon"><i className="fas fa-users-cog" /></div>
            <div className="content">
              <h3>Manager Tools</h3>
              <p>Residents, notices, events</p>
            </div>
          </Link>
        )}

        {isAdmin && (
          <Link to="/admin" className="dash-card">
            <div className="icon"><i className="fas fa-cogs" /></div>
            <div className="content">
              <h3>Admin Panel</h3>
              <p>Buildings, users, CMS, system tools</p>
            </div>
          </Link>
        )}
      </div>

      {user.ownerships && user.ownerships.length > 0 && (
        <div className="dash-section">
          <h2>My Properties</h2>
          <div className="dash-ownerships">
            {user.ownerships.map(o => (
              <div key={o.id} className="dash-own-card">
                <div className="top">
                  <span className="flat">{o.flat_number}</span>
                  <span className={`type ${o.ownership_type}`}>{o.ownership_type.toUpperCase()}</span>
                </div>
                <div className="sub">
                  <span>{o.building?.name || 'Building'}</span>
                  {o.flat?.flat_type && <span className="muted">{o.flat.flat_type}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isOwner && user.ownerships && (
        <div className="dash-section" id="flat-members">
          <h2>People in My Flat</h2>
          {membersLoading && <div className="muted">Loading members...</div>}
          {membersError && <div className="muted" style={{ color: '#dc2626' }}>{membersError}</div>}
          {!membersLoading && !membersError && (
            <div className="flat-people-wrap">
              {user.ownerships
                .filter(o => (o.ownership_type || '').toLowerCase() === 'owner')
                .map(o => {
                  const fid = o.flat?.id as number | undefined;
                  if (!fid) return null;
                  const members = Array.isArray(membersByFlat[fid]) ? membersByFlat[fid] : [];
                  const existingUserIds = new Set(members.map(m => m.user_id));
                  const candidates = (candidateUsers[fid] || []).filter(c => !existingUserIds.has(c.id));
                  return (
                    <div key={`flat-people-${fid}`} className="flat-people-card">
                      <div className="head">
                        <span className="flat">{o.flat_number}</span>
                        <span className="muted">{o.building?.name || ''}</span>
                        <div style={{ marginLeft: 'auto' }}>
                          <button className="btn btn-primary" onClick={() => openAddMember(fid, o.building?.id)}>
                            <i className="fas fa-user-plus"></i> Add
                          </button>
                        </div>
                      </div>
                      {addOpenForFlat === fid && (
                        <div className="add-member-row">
                          <select
                            value={addForm.user_id}
                            onChange={(e) => setAddForm(prev => ({ ...prev, user_id: Number(e.target.value) }))}
                          >
                            <option value="">Select user</option>
                            {candidates.map(u => (
                              <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                            ))}
                          </select>
                          <select
                            value={addForm.relation_type}
                            onChange={(e) => setAddForm(prev => ({ ...prev, relation_type: e.target.value }))}
                          >
                            <option value="tenant">Tenant</option>
                            <option value="wife">Wife</option>
                            <option value="husband">Husband</option>
                            <option value="son">Son</option>
                            <option value="daughter">Daughter</option>
                            <option value="parent">Parent</option>
                            <option value="relative">Relative</option>
                            <option value="servant">Servant</option>
                            <option value="guest">Guest</option>
                            <option value="other">Other</option>
                          </select>
                          <button className="btn btn-primary" onClick={() => submitAddMember(fid)}><i className="fas fa-check"></i></button>
                          <button className="btn btn-secondary" onClick={() => setAddOpenForFlat(null)}><i className="fas fa-times"></i></button>
                        </div>
                      )}
                      {!Array.isArray(members) || members.length === 0 ? (
                        <div className="muted">No members for this flat.</div>
                      ) : (
                        <ul className="people-list">
                          {members.map(m => (
                            <li key={m.id} className="person">
                              <div className="left">
                                <div className="avatar"><i className="fas fa-user" /></div>
                                <div className="info">
                                  <div className="name">{m.user?.name || 'User'}</div>
                                  <div className="sub">
                                    <span>{(m.relation_type || '').replace('_', ' ')}</span>
                                    <span className="dot">•</span>
                                    <span className={`badge ${m.status === 'approved' ? 'owner' : (m.status === 'pending' ? 'renter' : 'inactive')}`}>{m.status}</span>
                                  </div>
                                </div>
                              </div>
                              <div className="right">
                                <button className="btn btn-secondary" onClick={() => viewMemberProfile(m.user_id)} title="View Profile">
                                  <i className="fas fa-eye"></i>
                                </button>
                                &nbsp;
                                {m.status === 'pending' && (
                                  <button className="btn btn-primary" onClick={() => approveMember(fid, m.id)} title="Approve">
                                    <i className="fas fa-check"></i>
                                  </button>
                                )}
                                {m.status !== 'removed' && (
                                  <button className="btn btn-secondary" onClick={() => removeMember(fid, m.id)} title="Remove" style={{ marginLeft: 8 }}>
                                    <i className="fas fa-trash"></i>
                                  </button>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}

      {isOwner && user.ownerships && (
        <div className="dash-section" id="flat-users">
          <h2>Registered Users in My Flat</h2>
          {registeredLoading && <div className="muted">Loading users...</div>}
          {registeredError && <div className="muted" style={{ color: '#dc2626' }}>{registeredError}</div>}
          <div className="flat-people-wrap">
            {user.ownerships
              .filter(o => (o.ownership_type || '').toLowerCase() === 'owner')
              .map(o => {
                const fid = o.flat?.id as number | undefined;
                if (!fid) return null;
                // ensure loaded
                if (!registeredByFlat[fid]) {
                  // trigger load
                  if (!registeredLoading) {
                    (async () => {
                      setRegisteredLoading(true);
                      await refreshRegisteredUsers(fid);
                      await refreshOwners(fid);
                      setRegisteredLoading(false);
                    })();
                  }
                }
                const reg = Array.isArray(registeredByFlat[fid]) ? registeredByFlat[fid] : [];
                const memberIds = new Set((membersByFlat[fid] || []).map(m => m.user_id));
                const ownerIds = new Set((ownersByFlat[fid] || []));
                return (
                  <div key={`flat-reg-${fid}`} className="flat-people-card">
                    <div className="head">
                      <span className="flat">{o.flat_number}</span>
                      <span className="muted">{o.building?.name || ''}</span>
                    </div>
                    {reg.length === 0 ? (
                      <div className="muted">No registered users found.</div>
                    ) : (
                      <ul className="people-list">
                        {reg.map(u => {
                          // Ghost: registered in flat but not in memberships AND not an owner of the flat
                          const isGhost = !memberIds.has(u.id) && !ownerIds.has(u.id);
                          return (
                            <li key={`reg-${u.id}`} className="person">
                              <div className="left">
                                <div className="avatar"><i className="fas fa-user" /></div>
                                <div className="info">
                                  <div className="name">{u.name}</div>
                                  <div className="sub">
                                    <span>{(u.role || '').replace('_', ' ')}</span>
                                    <span className="dot">•</span>
                                    <span className={`badge ${u.is_active ? 'active' : 'inactive'}`}>{u.is_active ? 'active' : 'inactive'}</span>
                                    {isGhost && (<><span className="dot">•</span><span className="badge renter">ghost</span></>)}
                                  </div>
                                </div>
                              </div>
                              <div className="right">
                                <button className="btn btn-secondary" onClick={() => viewMemberProfile(u.id)} title="View Profile">
                                  <i className="fas fa-eye"></i>
                                </button>
                                &nbsp;
                                {u.is_active && (
                                  <button className="btn btn-secondary" onClick={async () => { await api.post(`/users/${u.id}/toggle-status`); await refreshRegisteredUsers(fid); }} title="Disable User">
                                    <i className="fas fa-user-slash"></i>
                                  </button>
                                )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {profileOpen && (
        <div className="dash-modal">
          <div className="dash-modal-content">
            <div className="dash-modal-header">
              <h3>Profile</h3>
              <button className="icon-btn" onClick={() => setProfileOpen(false)} aria-label="Close"><i className="fas fa-times"></i></button>
            </div>
            <div className="dash-modal-body">
              {profileLoading && <div className="muted">Loading...</div>}
              {profileError && <div className="muted" style={{ color:'#dc2626' }}>{profileError}</div>}
              {!profileLoading && !profileError && profileData && (
                <div className="profile-quick">
                  <div className="row"><strong>Name:</strong> <span>{profileData.name}</span></div>
                  <div className="row"><strong>Email:</strong> <span>{profileData.email}</span></div>
                  {profileData.phone && <div className="row"><strong>Phone:</strong> <span>{profileData.phone}</span></div>}
                  <div className="row"><strong>Role:</strong> <span>{profileData.role}</span></div>
                </div>
              )}
            </div>
            <div className="dash-modal-footer">
              <button className="btn btn-secondary" onClick={() => setProfileOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;


